import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { usePallet } from '../../contexts/PalletContext';

/**
 * Renderiza a resposta dos agentes de IA com diagramação de leitura.
 *
 * POR QUE UM RENDERIZADOR PRÓPRIO
 * Os modelos devolvem Markdown (títulos, negrito, listas, tabelas) e a exibição
 * anterior era <pre> com texto cru — o usuário via "## Título" e "**negrito**"
 * literalmente. Em vez de trazer uma biblioteca de Markdown, este componente
 * cobre o subconjunto que os agentes realmente emitem: nós controlamos o prompt,
 * então o conjunto é previsível e pequeno.
 *
 * SEGURANÇA: monta elementos React, nunca dangerouslySetInnerHTML. Texto vindo do
 * modelo não consegue injetar HTML.
 *
 * TEMA: herda a cor do container (`color: inherit`) e usa opacidade para tons
 * secundários, então funciona igual sobre fundo claro (Nota Técnica) e escuro
 * (SmartTalk) sem precisar de variante.
 */

interface RespostaIAProps {
    texto: string;
    /** Tamanho base do corpo. Padrão 15px: um ponto acima do texto da interface,
     *  o suficiente para leitura confortável de resposta longa sem destoar. */
    fontSize?: string;
}

/** Negrito, itálico, código e links dentro de uma linha. */
function renderInline(texto: string, chave: string, fontFamily: string): React.ReactNode[] {
    if (!texto) return [];

    // Ordem importa: código primeiro, para não formatar o que está dentro dele.
    const padrao = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))|(\*[^*\s][^*]*\*)|(https?:\/\/[^\s)<>]+)/g;

    const nos: React.ReactNode[] = [];
    let ultimo = 0;
    let m: RegExpExecArray | null;
    let i = 0;

    while ((m = padrao.exec(texto)) !== null) {
        if (m.index > ultimo) nos.push(texto.slice(ultimo, m.index));
        const t = m[0];
        const k = `${chave}-i${i++}`;

        if (t.startsWith('`')) {
            nos.push(
                <Box
                    key={k}
                    component="code"
                    sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: '0.88em',
                        px: '5px',
                        py: '1px',
                        borderRadius: '4px',
                        bgcolor: 'rgba(127,127,127,0.18)',
                    }}
                >
                    {t.slice(1, -1)}
                </Box>,
            );
        } else if (t.startsWith('**')) {
            nos.push(<Box key={k} component="strong" sx={{ fontWeight: 600 }}>{t.slice(2, -2)}</Box>);
        } else if (t.startsWith('[')) {
            const corte = t.indexOf('](');
            const rotulo = t.slice(1, corte);
            const url = t.slice(corte + 2, -1);
            nos.push(
                <Box key={k} component="a" href={url} target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '2px', opacity: 0.9 }}>
                    {rotulo}
                </Box>,
            );
        } else if (t.startsWith('*')) {
            nos.push(<Box key={k} component="em" sx={{ fontStyle: 'italic' }}>{t.slice(1, -1)}</Box>);
        } else {
            nos.push(
                <Box key={k} component="a" href={t} target="_blank" rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: '2px', opacity: 0.9, wordBreak: 'break-all' }}>
                    {t}
                </Box>,
            );
        }
        ultimo = m.index + t.length;
    }
    if (ultimo < texto.length) nos.push(texto.slice(ultimo));
    return nos;
}

export const RespostaIA: React.FC<RespostaIAProps> = ({ texto, fontSize = '15px' }) => {
    const { pallet } = usePallet();
    const ff = pallet.general.fontFamily;

    if (!texto) return null;

    const linhas = String(texto).replace(/\r\n/g, '\n').split('\n');
    const blocos: React.ReactNode[] = [];

    let paragrafo: string[] = [];
    let lista: { tipo: 'ol' | 'ul'; itens: string[] } | null = null;
    let bloco: string[] | null = null;   // dentro de ``` ```
    let tabela: string[] | null = null;
    let n = 0;

    const fecharParagrafo = () => {
        if (!paragrafo.length) return;
        const conteudo = paragrafo.join(' ');
        blocos.push(
            <Typography key={`p${n++}`} sx={{ fontFamily: ff, fontSize, lineHeight: 1.75, mb: 1.5, color: 'inherit' }}>
                {renderInline(conteudo, `p${n}`, ff)}
            </Typography>,
        );
        paragrafo = [];
    };

    const fecharLista = () => {
        if (!lista || !lista.itens.length) { lista = null; return; }
        const { tipo, itens } = lista;
        blocos.push(
            <Box key={`l${n++}`} component={tipo} sx={{ pl: 3, mb: 1.5, mt: 0, '& li': { mb: 0.8 } }}>
                {itens.map((it, idx) => (
                    <Box key={idx} component="li" sx={{ fontFamily: ff, fontSize, lineHeight: 1.7, color: 'inherit' }}>
                        {renderInline(it, `l${n}-${idx}`, ff)}
                    </Box>
                ))}
            </Box>,
        );
        lista = null;
    };

    const fecharTabela = () => {
        if (!tabela || tabela.length < 2) { tabela = null; return; }
        const celulas = (linha: string) =>
            linha.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());
        // A 2ª linha é o separador (|---|---|) e não vira conteúdo.
        const cabecalho = celulas(tabela[0]);
        const corpo = tabela.slice(2).map(celulas);

        blocos.push(
            <Box key={`t${n++}`} sx={{ overflowX: 'auto', mb: 2 }}>
                <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', fontFamily: ff, fontSize }}>
                    <Box component="thead">
                        <Box component="tr">
                            {cabecalho.map((c, i) => (
                                <Box component="th" key={i}
                                    sx={{ textAlign: 'left', p: '8px 10px', fontWeight: 600, borderBottom: '2px solid rgba(127,127,127,0.35)', whiteSpace: 'nowrap' }}>
                                    {renderInline(c, `th${i}`, ff)}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {corpo.map((linha, li) => (
                            <Box component="tr" key={li}>
                                {linha.map((c, ci) => (
                                    <Box component="td" key={ci}
                                        sx={{ p: '8px 10px', borderBottom: '1px solid rgba(127,127,127,0.18)', verticalAlign: 'top', lineHeight: 1.6 }}>
                                        {renderInline(c, `td${li}-${ci}`, ff)}
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>,
        );
        tabela = null;
    };

    const fecharTudo = () => { fecharParagrafo(); fecharLista(); fecharTabela(); };

    for (let idx = 0; idx < linhas.length; idx++) {
        const linhaBruta = linhas[idx];
        const linha = linhaBruta.replace(/\s+$/, '');

        // Bloco de código: tudo entre ``` sai literal, sem formatação inline.
        if (/^\s*```/.test(linha)) {
            if (bloco === null) { fecharTudo(); bloco = []; }
            else {
                blocos.push(
                    <Box key={`c${n++}`} component="pre"
                        sx={{
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                            fontSize: '0.86em', lineHeight: 1.6, p: 1.5, mb: 1.5, mt: 0,
                            borderRadius: '6px', bgcolor: 'rgba(127,127,127,0.15)',
                            overflowX: 'auto', whiteSpace: 'pre',
                        }}>
                        {bloco.join('\n')}
                    </Box>,
                );
                bloco = null;
            }
            continue;
        }
        if (bloco !== null) { bloco.push(linhaBruta); continue; }

        // Tabela: linhas consecutivas começando e terminando com |
        if (/^\s*\|.*\|\s*$/.test(linha)) {
            fecharParagrafo(); fecharLista();
            if (!tabela) tabela = [];
            tabela.push(linha);
            continue;
        }
        if (tabela) fecharTabela();

        if (!linha.trim()) {
            fecharParagrafo();
            // O modelo costuma separar itens de lista com linha em branco (lista
            // "solta"). Fechar a lista aqui faria cada item virar um <ol>/<ul> de
            // item único — e o navegador reinicia a numeração em 1 a cada um.
            // Só fecha de fato se a próxima linha não útil não continuar a mesma lista.
            const proxima = linhas.slice(idx + 1).find(l => l.trim());
            const continuaLista = lista && proxima != null && (
                lista.tipo === 'ol' ? /^\s*\d+[.)]\s+/.test(proxima) : /^\s*[-*•]\s+/.test(proxima)
            );
            if (!continuaLista) fecharLista();
            continue;
        }

        // Separador horizontal
        if (/^\s*(---+|___+|\*\*\*+)\s*$/.test(linha)) {
            fecharTudo();
            blocos.push(<Divider key={`d${n++}`} sx={{ my: 2, borderColor: 'rgba(127,127,127,0.28)' }} />);
            continue;
        }

        // Títulos
        const tit = linha.match(/^(#{1,6})\s+(.*)$/);
        if (tit) {
            fecharTudo();
            const nivel = tit[1].length;
            const tamanhos: Record<number, string> = { 1: '1.28em', 2: '1.16em', 3: '1.06em' };
            blocos.push(
                <Typography key={`h${n++}`}
                    sx={{
                        fontFamily: ff,
                        fontSize: tamanhos[nivel] || '1em',
                        fontWeight: 600,
                        lineHeight: 1.4,
                        mt: blocos.length ? 2.2 : 0,
                        mb: 1,
                        color: 'inherit',
                    }}>
                    {renderInline(tit[2], `h${n}`, ff)}
                </Typography>,
            );
            continue;
        }

        // Citação
        const cit = linha.match(/^\s*>\s?(.*)$/);
        if (cit) {
            fecharTudo();
            blocos.push(
                <Box key={`q${n++}`}
                    sx={{ borderLeft: '3px solid rgba(127,127,127,0.4)', pl: 1.5, py: 0.3, mb: 1.5, opacity: 0.9 }}>
                    <Typography sx={{ fontFamily: ff, fontSize, lineHeight: 1.7, fontStyle: 'italic', color: 'inherit' }}>
                        {renderInline(cit[1], `q${n}`, ff)}
                    </Typography>
                </Box>,
            );
            continue;
        }

        // Lista ordenada / não ordenada
        const ol = linha.match(/^\s*\d+[.)]\s+(.*)$/);
        const ul = linha.match(/^\s*[-*•]\s+(.*)$/);
        if (ol || ul) {
            fecharParagrafo();
            const tipo: 'ol' | 'ul' = ol ? 'ol' : 'ul';
            if (lista && lista.tipo !== tipo) fecharLista();
            if (!lista) lista = { tipo, itens: [] };
            lista.itens.push((ol ? ol[1] : ul![1]));
            continue;
        }

        // Continuação de item de lista (linha indentada logo após um item)
        if (lista && /^\s{2,}\S/.test(linhaBruta)) {
            lista.itens[lista.itens.length - 1] += ` ${linha.trim()}`;
            continue;
        }

        fecharLista();
        paragrafo.push(linha.trim());
    }

    if (bloco !== null && bloco.length) {
        blocos.push(
            <Box key={`c${n++}`} component="pre"
                sx={{
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: '0.86em', lineHeight: 1.6, p: 1.5, mb: 1.5,
                    borderRadius: '6px', bgcolor: 'rgba(127,127,127,0.15)',
                    overflowX: 'auto', whiteSpace: 'pre',
                }}>
                {bloco.join('\n')}
            </Box>,
        );
    }
    fecharTudo();

    // O último bloco não deve empurrar espaço extra no fim da mensagem.
    return <Box sx={{ color: 'inherit', '& > *:last-child': { mb: 0 } }}>{blocos}</Box>;
};

export default RespostaIA;
